import React, { useState } from 'react';
import { 
  Save, 
  X, 
  AlertCircle,
  CheckCircle
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { groundsService } from '../services/supabaseService';
import PageLayout from './PageLayout';
import '../styles/CreateGround.css';

const CreateGround = () => {
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});


  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    groundName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.groundName.trim()) {
      newErrors.groundName = 'Ground name is required';
    }

    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State / Province / Region is required';
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'Zip code is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      await groundsService.create(formData);
      
      setShowSuccess(true);
      
      setTimeout(() => {
        navigate('/ViewGrounds');
      }, 2000);
      
    } catch (error) {
      console.error('Error creating ground:', error);
      setErrors({ submit: 'Failed to create ground. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/ViewGrounds');
    }
  };

  return (
    <PageLayout className="create-ground-page">
      <div className="cg-header">
        <div className="cg-header-left">
          <h1 className="cg-title">Create New Venue</h1>
          <p className="cg-subtitle">Add a new sports venue to your facility</p>
        </div>
      </div>

      {showSuccess && (
        <div className="cg-success-message">
          <CheckCircle size={20} />
          <span>Venue created successfully! Redirecting...</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="cg-form">
        <div className="cg-form-section">
          <h2 className="cg-section-title">Venue Information</h2>
          
          <div className="cg-form-group">
            <label className="cg-form-label">Venue Name *</label>
            <input
              type="text"
              value={formData.groundName}
              onChange={(e) => handleInputChange('groundName', e.target.value)}
              className={`cg-form-input ${errors.groundName ? 'cg-error' : ''}`}
              placeholder="Enter ground name"
            />
            {errors.groundName && (
              <span className="cg-error-message">
                <AlertCircle size={16} />
                {errors.groundName}
              </span>
            )}
          </div>

          <div className="cg-form-group">
            <label className="cg-form-label">Address Line 1 *</label>
            <input
              type="text"
              value={formData.address1}
              onChange={(e) => handleInputChange('address1', e.target.value)}
              className={`cg-form-input ${errors.address1 ? 'cg-error' : ''}`}
              placeholder="Enter street address"
            />
            {errors.address1 && (
              <span className="cg-error-message">
                <AlertCircle size={16} />
                {errors.address1}
              </span>
            )}
          </div>

          <div className="cg-form-group">
            <label className="cg-form-label">Address Line 2</label>
            <input
              type="text"
              value={formData.address2}
              onChange={(e) => handleInputChange('address2', e.target.value)}
              className="cg-form-input"
              placeholder="Apartment, suite, unit, building, floor, etc. (optional)"
            />
          </div>

          <div className="cg-form-grid">
            <div className="cg-form-group">
              <label className="cg-form-label">City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className={`cg-form-input ${errors.city ? 'cg-error' : ''}`}
                placeholder="Enter city"
              />
              {errors.city && (
                <span className="cg-error-message">
                  <AlertCircle size={16} />
                  {errors.city}
                </span>
              )}
            </div>

            {/* State as text input */}
            <div className="cg-form-group">
              <label className="cg-form-label">State / Province / Region *</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                className={`cg-form-input ${errors.state ? 'cg-error' : ''}`}
                placeholder="Enter state, province, or region"
              />
              {errors.state && (
                <span className="cg-error-message">
                  <AlertCircle size={16} />
                  {errors.state}
                </span>
              )}
            </div>

            <div className="cg-form-group">
              <label className="cg-form-label">Zip Code *</label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                className={`cg-form-input ${errors.zipCode ? 'cg-error' : ''}`}
                placeholder="Enter zip code"
              />
              {errors.zipCode && (
                <span className="cg-error-message">
                  <AlertCircle size={16} />
                  {errors.zipCode}
                </span>
              )}
            </div>

            <div className="cg-form-group">
              <label className="cg-form-label">Country *</label>
              <select
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className={`cg-form-select ${errors.country ? 'cg-error' : ''}`}
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Other">Other</option>
              </select>
              {errors.country && (
                <span className="cg-error-message">
                  <AlertCircle size={16} />
                  {errors.country}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="cg-form-actions">
          {errors.submit && (
            <div className="cg-submit-error">
              <AlertCircle size={20} />
              <span>{errors.submit}</span>
            </div>
          )}
          
          <div className="cg-action-buttons">
            <button
              type="button"
              onClick={handleCancel}
              className="cg-cancel-btn"
              disabled={loading}
            >
              <X size={18} />
              <span>Cancel</span>
            </button>
            
            <button
              type="submit"
              className="cg-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="cg-loading-spinner"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>Create Venue</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </PageLayout>
  );
};

export default CreateGround;
